import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { PrepLessonDetails as PrepLessonDetailsServerSide } from '@yandex-int/k-common';
import { Lesson } from '@yandex-int/k-common';
import { LOADING_STATUSES } from '@yandex-int/k-common/client/api/constants';
import get from 'lodash/get';
import uniqBy from 'lodash/uniqBy';
import { DEFAULT_PAGE_TO_LOAD } from 'platform-components/constants';

import { PrepLessonsStoreState } from './typings';
import { getPrepLessonsDetailsLoadingStatusKey } from './utils';

const initialState: PrepLessonsStoreState = {
    prepLessons: {},
    prepLessonsLoadedPages: {},
    prepLessonsLoadingStatus: {},
    canLoadMorePrepLessons: {},
    prepLessonsDetails: {},
    prepLessonsDetailsLoadingStatus: {},
};

type PayloadProps = {
    courseId: number;
    lessonsIds: number[];
};

const slice = createSlice({
    name: 'PREP_LESSONS',
    initialState: initialState,
    reducers: {
        loadPrepLessonsRequest: (
            state: PrepLessonsStoreState,
            action: PayloadAction<{
                themeId: number;
                page: number;
                pageSize?: number;
            }>
        ) => {
            const { themeId } = action.payload;

            state.prepLessonsLoadingStatus[themeId] = LOADING_STATUSES.LOADING;
        },
        loadPrepLessonsError: (
            state: PrepLessonsStoreState,
            action: PayloadAction<{
                error: any;
                themeId: number;
            }>
        ) => {
            const { themeId } = action.payload;
            state.prepLessonsLoadingStatus[themeId] = LOADING_STATUSES.ERROR;
        },
        loadPrepLessonsSuccess: (
            state: PrepLessonsStoreState,
            action: PayloadAction<{
                lessons: Lesson[];
                themeId: number;
                page: number;
                canLoadMore: boolean;
            }>
        ) => {
            const { lessons, themeId, page, canLoadMore } = action.payload;

            const stateLessons = get(state, ['prepLessons', themeId], [] as Lesson[]);

            if (page === DEFAULT_PAGE_TO_LOAD) {
                state.prepLessons[themeId] = lessons;
            } else {
                state.prepLessons[themeId] = uniqBy(stateLessons.concat(lessons), 'id');
            }

            state.prepLessonsLoadingStatus[themeId] = LOADING_STATUSES.SUCCESS;
            state.prepLessonsLoadedPages[themeId] = page;
            state.canLoadMorePrepLessons[themeId] = canLoadMore;
        },
        loadPrepLessonsDetailsRequest: (
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            state: PrepLessonsStoreState,
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            action: PayloadAction<PayloadProps>
        ) => {},
        loadPrepLessonsDetailsLoading: (state: PrepLessonsStoreState, action: PayloadAction<PayloadProps>) => {
            const key = getKey(action.payload);
            state.prepLessonsDetailsLoadingStatus[key] = LOADING_STATUSES.LOADING;
        },
        loadPrepLessonsDetailsError: (
            state: PrepLessonsStoreState,
            action: PayloadAction<
                PayloadProps & {
                    error: any;
                }
            >
        ) => {
            const { lessonsIds, courseId } = action.payload;
            lessonsIds.forEach((lessonId) => {
                const key = getKey({ courseId, lessonsIds: [lessonId] });
                state.prepLessonsDetailsLoadingStatus[key] = LOADING_STATUSES.ERROR;
            });
            const key = getKey(action.payload);
            state.prepLessonsDetailsLoadingStatus[key] = LOADING_STATUSES.ERROR;
        },
        loadPrepLessonsDetailsSuccess: (
            state: PrepLessonsStoreState,
            action: PayloadAction<
                PayloadProps & {
                    details: PrepLessonDetailsServerSide[];
                }
            >
        ) => {
            const { details, courseId } = action.payload;

            if (!get(state, ['prepLessonsDetails', courseId])) {
                state.prepLessonsDetails[courseId] = {};
            }

            details.forEach((item) => {
                state.prepLessonsDetails[courseId][item.id] = {
                    assignedCount: item.assigned_count,
                    problemsCount: item.problems_count,
                };
            });

            const { lessonsIds } = action.payload;
            lessonsIds.forEach((lessonId) => {
                const key = getKey({ courseId, lessonsIds: [lessonId] });
                state.prepLessonsDetailsLoadingStatus[key] = LOADING_STATUSES.SUCCESS;
            });
            const key = getKey(action.payload);
            state.prepLessonsDetailsLoadingStatus[key] = LOADING_STATUSES.SUCCESS;
        },
        clearPrepLessonsDetails: (state: PrepLessonsStoreState) => {
            state.prepLessonsDetails = {};
            state.prepLessonsDetailsLoadingStatus = {};
        },
    },
});

const getKey = (payload: PayloadProps) => {
    const { lessonsIds, courseId } = payload;
    return getPrepLessonsDetailsLoadingStatusKey(courseId, lessonsIds);
};

export const prepLessonsActions = slice.actions;
export const prepLessonsReducer = slice.reducer;
